import {useState} from 'react';
import {View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Alert} from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import {useTranslation} from 'react-i18next';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {useInviteConfig} from '@/data/invitation-hooks';
import {useOrganizationNavigation} from '@/hooks/useOrganizationNavigation';
import {HeaderText} from '@/components/common/HeaderText';
import {ThemeType} from '@/theming/baseTheme';
import {createQrInvitation} from '@/data/invitation-api';
import {pb} from '@/lib/pocketbase';

interface QrInviteScreenProps {
    orgId: string;
}

interface QrResult {
    url: string;
    planName: string;
}

export const QrInviteScreen = ({orgId}: QrInviteScreenProps) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const {data: inviteConfig} = useInviteConfig(orgId);
    const navigation = useOrganizationNavigation(orgId);
    const styles = createStyles(theme);

    const [selectedGroup, setSelectedGroup] = useState<string | undefined>(undefined);
    const [qrResult, setQrResult] = useState<QrResult | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);

    const configs = inviteConfig.membershipConfigurations;
    const groups = inviteConfig.groups;

    // Auto-select if only one group
    const effectiveGroup = selectedGroup || (groups.length === 1 ? groups[0].id : undefined);

    const handleSelectPlan = async (configId: string, configName: string) => {
        setIsGenerating(true);
        try {
            const userId = pb.authStore.record?.id;
            if (!userId) return;

            const result = await createQrInvitation({
                organization: orgId,
                invitedBy: userId,
                membershipConfiguration: configId,
                group: effectiveGroup,
            });
            setQrResult({url: result.url, planName: configName});
        } catch {
            Alert.alert(t('invite.error.createFailed'));
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCreateAnother = () => {
        setQrResult(null);
        setSelectedGroup(undefined);
    };

    // QR Display state
    if (qrResult) {
        return (
            <View style={styles.container}>
                <HeaderText title={t('invite.title')} onBackPress={navigation.navigateBack}/>
                <View style={styles.content}>
                    <Text style={styles.planTitle}>{qrResult.planName}</Text>
                    <View style={styles.qrContainer}>
                        <QRCode
                            value={qrResult.url}
                            size={250}
                            color={theme.colors.text}
                            backgroundColor={theme.colors.surface}
                        />
                    </View>
                    <Text style={styles.scanText}>{t('invite.scanToJoin')}</Text>
                    <TouchableOpacity style={styles.primaryButton} onPress={handleCreateAnother}>
                        <Text style={styles.primaryButtonText}>{t('invite.createAnother')}</Text>
                    </TouchableOpacity>
                </View>
            </View>
        );
    }

    // Loading state
    if (isGenerating) {
        return (
            <View style={styles.container}>
                <HeaderText title={t('invite.title')} onBackPress={navigation.navigateBack}/>
                <View style={styles.content}>
                    <ActivityIndicator size="large" color={theme.colors.primary}/>
                    <Text style={styles.generatingText}>{t('invite.generating')}</Text>
                </View>
            </View>
        );
    }

    // Group selection state (multiple groups: user must pick)
    if (groups.length > 1 && !selectedGroup) {
        return (
            <View style={styles.container}>
                <HeaderText title={t('invite.title')} onBackPress={navigation.navigateBack}/>
                <View style={styles.content}>
                    <Text style={styles.sectionTitle}>{t('invite.selectGroup')}</Text>
                    {groups.map(group => (
                        <TouchableOpacity
                            key={group.id}
                            style={styles.card}
                            onPress={() => setSelectedGroup(group.id)}
                        >
                            <Text style={styles.cardTitle}>{group.name}</Text>
                        </TouchableOpacity>
                    ))}
                </View>
            </View>
        );
    }

    // Plan selection state
    if (configs.length === 0) {
        return (
            <View style={styles.container}>
                <HeaderText title={t('invite.title')} onBackPress={navigation.navigateBack}/>
                <View style={styles.content}>
                    <Text style={styles.emptyText}>{t('invite.error.noPlans')}</Text>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <HeaderText title={t('invite.title')} onBackPress={navigation.navigateBack}/>
            <View style={styles.content}>
                {groups.length === 1 && (
                    <Text style={styles.groupIndicator}>{groups[0].name}</Text>
                )}
                <Text style={styles.sectionTitle}>{t('invite.selectPlan')}</Text>
                {configs.map(config => (
                    <TouchableOpacity
                        key={config.id}
                        style={styles.card}
                        onPress={() => handleSelectPlan(config.id, config.name)}
                    >
                        <View style={styles.cardHeader}>
                            <Text style={styles.cardTitle}>{config.name}</Text>
                            {config.isFree ? (
                                <View style={styles.freeBadge}>
                                    <Text style={styles.freeBadgeText}>FREE</Text>
                                </View>
                            ) : (
                                <Text style={styles.cardPrice}>
                                    {config.currency} {config.price.toFixed(2)} / {config.period}
                                </Text>
                            )}
                        </View>
                    </TouchableOpacity>
                ))}
            </View>
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    content: {
        flex: 1,
        padding: theme.spacing.md,
        alignItems: 'center',
    },
    groupIndicator: {
        fontSize: theme.fontSize.sm,
        color: theme.colors.textSecondary,
        marginBottom: theme.spacing.sm,
        alignSelf: 'flex-start',
    },
    sectionTitle: {
        fontSize: theme.fontSize.lg,
        fontWeight: '600',
        color: theme.colors.text,
        marginBottom: theme.spacing.md,
        alignSelf: 'flex-start',
    },
    card: {
        width: '100%',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.sm,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    cardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    cardTitle: {
        fontSize: theme.fontSize.md,
        fontWeight: '600',
        color: theme.colors.text,
    },
    cardPrice: {
        fontSize: theme.fontSize.sm,
        color: theme.colors.textSecondary,
    },
    freeBadge: {
        backgroundColor: theme.colors.primary,
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: 2,
        borderRadius: theme.borderRadius.sm,
    },
    freeBadgeText: {
        color: '#fff',
        fontSize: theme.fontSize.xs,
        fontWeight: '700',
    },
    qrContainer: {
        padding: theme.spacing.lg,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        marginVertical: theme.spacing.lg,
    },
    planTitle: {
        fontSize: theme.fontSize.xl,
        fontWeight: '700',
        color: theme.colors.text,
        marginTop: theme.spacing.lg,
    },
    scanText: {
        fontSize: theme.fontSize.md,
        color: theme.colors.textSecondary,
        marginBottom: theme.spacing.lg,
    },
    generatingText: {
        fontSize: theme.fontSize.md,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.md,
    },
    emptyText: {
        fontSize: theme.fontSize.md,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.xl,
        textAlign: 'center',
    },
    primaryButton: {
        backgroundColor: theme.colors.primary,
        paddingHorizontal: theme.spacing.xl,
        paddingVertical: theme.spacing.sm,
        borderRadius: theme.borderRadius.md,
    },
    primaryButtonText: {
        color: '#fff',
        fontSize: theme.fontSize.md,
        fontWeight: '600',
    },
});
